import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import queryString from 'query-string';

import { SSO_LOGIN_URL, SSO_LOGOUT_URL } from '../services/sso.config';
import { fetchTokens, getUserSession } from '../services/auth.service';
import { getLocalStorage, setLocalStorage, isTokenExpired } from '../utils/localstorage';
import Loader from '../components/loader-component';
import { notification } from 'antd';
import { amplitudeLogEvent } from '../utils/amplitudeEvent';

const Login = () => {
  const navigate = useNavigate();
  const [isCropwise] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('tokens');
    window.location.href = SSO_LOGOUT_URL;
  };

  const getUserSessionData = async () => {
    try {
      const { userSession, isAdmin, status = null } = await getUserSession();

      // Handle 401/403 - unauthorized
      if (!userSession && status && [401, 403].includes(status)) {
        setIsLoading(false);
        notification.error({
          message: 'User Not Found',
          description: 'The user session could not be found. Please log in again.'
        });
        amplitudeLogEvent('Login Failed', {
          reason: 'User session not found',
          status: status,
          user: userSession
        });
        handleLogout();
        return;
      }

      // Handle case where userSession is null but no specific error status
      if (!userSession) {
        setIsLoading(false);
        notification.error({
          message: 'Login Error',
          description: 'Unable to retrieve user session. Please try again.'
        });
        amplitudeLogEvent('Login Failed', {
          reason: 'User session null',
          status: status
        });
        handleLogout();
        return;
      }

      // Success - navigate based on role
      if (isAdmin) {
        void navigate('/app/admin', { state: { fromLogin: true, userType: 'admin', userSession } });
      } else {
        void navigate('/app/dashboard', {
          state: { fromLogin: true, userType: 'user', userSession }
        });
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error getting user session:', error);
      setIsLoading(false);

      // For network/connection errors, don't clear tokens - navigate to dashboard
      // Dashboard has retry UI to handle this gracefully
      amplitudeLogEvent('Login Failed', {
        reason: 'Network/Server error',
        error: error?.message
      });

      // Navigate to dashboard - it will show error UI with retry button
      // Keep tokens so user can retry without re-authenticating
      void navigate('/app/dashboard', {
        state: {
          fromLogin: true,
          userType: 'user',
          connectionError: true,
          errorMessage: 'Unable to connect to the server. Please check your network and try again.'
        }
      });
    }
  };

  // Function to fetch tokens using the OAuth code
  const getLoginRes = async (oauth_code) => {
    try {
      const res = await fetchTokens(oauth_code);
      if (res) {
        // Store tokens with created_at for expiry tracking
        const tokensWithTimestamp = {
          ...res,
          created_at: res.created_at || Math.floor(Date.now() / 1000)
        };
        await setLocalStorage('tokens', tokensWithTimestamp);
        // get user session
        void getUserSessionData();
      } else {
        console.warn('No response from token fetch');
        setIsLoading(false);
        notification.error({
          message: 'Login Error',
          description: 'Failed to fetch tokens. Please try logging in again.'
        });
        amplitudeLogEvent('Login Failed', {
          reason: 'No response from token fetch'
        });
        // Redirect to SSO logout then login
        handleLogout();
      }
    } catch (e) {
      console.warn('token err', e);
      setIsLoading(false);
      notification.error({
        message: 'Login Error',
        description: 'Failed to fetch tokens. Please try logging in again.'
      });
      amplitudeLogEvent('Login Failed', {
        reason: 'Token fetch error',
        error: e.message
      });
      // Redirect to SSO login if token fetch fails
      window.location.href = SSO_LOGIN_URL;
    }
  };

  useEffect(() => {
    const query = queryString.parse(window.location.search);
    const oauth_code = query?.code;
    const returnedState = query?.state as string;

    // Check for valid non-expired tokens first
    const hasValidTokens = getLocalStorage('tokens') && !isTokenExpired();

    if (hasValidTokens) {
      setIsLoading(true);
      void getUserSessionData();
      return;
    }

    // Handle OAuth callback with code
    if (oauth_code) {
      // CSRF Protection: Verify OAuth state parameter if present
      const savedState = sessionStorage.getItem('oauth_state');

      // Only validate state if we have a saved state (we initiated the flow)
      if (savedState && returnedState && returnedState !== savedState) {
        console.error('OAuth state mismatch - possible CSRF attack');
        notification.error({
          message: 'Security Error',
          description: 'Authentication failed due to security validation. Please try again.'
        });
        amplitudeLogEvent('Login Failed', { reason: 'CSRF state mismatch' });
        sessionStorage.removeItem('oauth_state');
        // Clear and restart
        localStorage.removeItem('tokens');
        window.location.href = SSO_LOGIN_URL;
        return;
      }

      // Clear state after validation
      sessionStorage.removeItem('oauth_state');

      setIsLoading(true);
      void getLoginRes(oauth_code);
      return;
    }

    // No tokens and no OAuth code - redirect to SSO
    if (isCropwise) {
      // Clear any expired tokens
      localStorage.removeItem('tokens');

      // Generate and store OAuth state for CSRF protection
      const state = crypto.randomUUID();
      sessionStorage.setItem('oauth_state', state);

      setIsLoading(true);
      window.location.href = `${SSO_LOGIN_URL}&state=${state}`;
    }
  }, [isCropwise, navigate]);

  if (isLoading) {
    return <Loader />;
  }
  return <></>;
};

export default Login;
